/**
 * catalogEntryCache — ONE workflow's FULL catalog entry, read at most once per
 * pane session (plugin catalog round, 2026-10-06).
 *
 * The pane lists the catalog COMPACT (`getCatalog({ compact: true })`): names,
 * descriptions and card fields, no input_schema / steps / ui_metadata /
 * manifest — those were ~97% of an 81 KB read that ran on every pane open and
 * every Launch open. A launch form and a run's step list need the full entry,
 * so they read it here, from the API's single-entry route. This module lives as
 * long as the pane's JS does, so "cached per pane session" is a module Map.
 *
 * The rule it exists for: a COMPACT entry never reaches a form or a step list.
 * `isCompactCatalogEntry` is the ONE test. An entry without the marker is full
 * — every entry an API that predates compact sends — and needs no read.
 *
 * Failure. A 404 means the API has no single-entry route (an older API) or no
 * longer lists the workflow: the FULL list is read once instead and every full
 * entry in it is kept. Any other failure rejects — the caller shows an error
 * with Try again — and is not cached, so the next call reads again. Neither
 * path ever hands back the compact entry.
 */
import { orchestratorApi } from "./orchestratorApi";
import type { WorkflowCatalogEntry } from "../types/workflow";

const entries = new Map<string, WorkflowCatalogEntry>();
const inflight = new Map<string, Promise<WorkflowCatalogEntry>>();

/** True on an entry of the compact list — the server marks every one. */
export function isCompactCatalogEntry(
  entry: Pick<WorkflowCatalogEntry, "compact"> | null | undefined,
): boolean {
  return entry?.compact === true;
}

/** The full entry already read this session, if any. */
export function peekFullCatalogEntry(workflowId: string): WorkflowCatalogEntry | undefined {
  return entries.get(workflowId);
}

function isNotFound(err: unknown): boolean {
  return (err as { status?: unknown } | null)?.status === 404;
}

/** ONE full-list fallback read shared by every concurrent 404; cleared when it settles. */
let fullList: Promise<void> | null = null;

function readFullList(): Promise<void> {
  fullList ??= orchestratorApi
    .getCatalog()
    .then((list) => {
      // Never replace an object already handed out: consumers key effects on it.
      for (const full of list) {
        if (!isCompactCatalogEntry(full) && !entries.has(full.id)) entries.set(full.id, full);
      }
    })
    .finally(() => {
      fullList = null;
    });
  return fullList;
}

async function readFull(workflowId: string): Promise<WorkflowCatalogEntry> {
  let entry: WorkflowCatalogEntry | undefined;
  try {
    entry = await orchestratorApi.getCatalogEntry(workflowId);
  } catch (err) {
    if (!isNotFound(err)) throw err;
    await readFullList();
    entry = entries.get(workflowId);
    if (!entry) throw new Error(`The workflow catalog no longer lists ${workflowId}.`);
  }
  if (isCompactCatalogEntry(entry)) {
    throw new Error(`catalog entry ${workflowId}: the API sent a compact entry`);
  }
  // A concurrent fallback may have stored this id already: keep that object.
  const kept = entries.get(workflowId) ?? entry;
  entries.set(workflowId, kept);
  return kept;
}

/** The FULL entry for `workflowId`: cached, shared with a read in flight, or read now. */
export function loadFullCatalogEntry(workflowId: string): Promise<WorkflowCatalogEntry> {
  const hit = entries.get(workflowId);
  if (hit) return Promise.resolve(hit);
  let pending = inflight.get(workflowId);
  if (!pending) {
    const created: Promise<WorkflowCatalogEntry> = readFull(workflowId).finally(() => {
      // A read from before a reset must not delete a newer in-flight entry.
      if (inflight.get(workflowId) === created) inflight.delete(workflowId);
    });
    pending = created;
    inflight.set(workflowId, pending);
  }
  return pending;
}

/** Tests only — the cache otherwise lives for the pane session. */
export function _resetCatalogEntryCacheForTests(): void {
  entries.clear();
  inflight.clear();
  fullList = null;
}
